import { apiClient } from './api';

export type MediaKind = 'image' | 'video' | 'product' | 'logo';

const SIZE_LIMITS: Record<MediaKind, number> = {
  image: 10 * 1024 * 1024,    // 10MB
  product: 5 * 1024 * 1024,   // 5MB
  logo: 5 * 1024 * 1024,      // 5MB
  video: 30 * 1024 * 1024,    // 30MB — teto prático: o prefetch do player tem
                              // timeout de 240s por arquivo (tv-app mediaCache.ts)
                              // e o presigned PUT expira em 30min
};

const MIME_HINTS: Record<MediaKind, RegExp> = {
  image: /^image\//,
  product: /^image\//,
  logo: /^image\//,
  video: /^video\//,
};

// Otimização de imagem para TV signage:
//  - max 1920px no lado maior (mais que isso o decoder de TV box trava — Glide
//    em Android 7.1/API25 falha silencioso em PNG >~1.5MB compactado, e bitmap
//    >4K estoura heap)
//  - PNG opaco → JPG q85 (arquivo 3–10x menor, decoder mais leve)
//  - PNG com transparência → mantém PNG (logo / overlay)
//  - GIF / SVG / WebP → não toca (anim / vetor)
const IMAGE_MAX_SIDE = 1920;
const IMAGE_JPEG_QUALITY = 0.85;
const IMAGE_WEBP_QUALITY = 0.82; // WebP q82 ≈ JPEG q85 visual, ~30% menor
const SKIP_OPTIMIZE = new Set(['image/gif', 'image/svg+xml']);

// Detecta suporte a encoding WebP no browser. Canvas.toBlob('image/webp')
// retorna PNG silenciosamente em browsers sem suporte (Safari < 14, IE).
// Cacheado pra rodar uma vez por sessão.
let webpSupportPromise: Promise<boolean> | null = null;
function browserCanEncodeWebP(): Promise<boolean> {
  if (webpSupportPromise) return webpSupportPromise;
  webpSupportPromise = new Promise((resolve) => {
    const c = document.createElement('canvas');
    c.width = c.height = 2;
    c.toBlob(
      (b) => resolve(!!b && b.type === 'image/webp'),
      'image/webp',
      0.5,
    );
  });
  return webpSupportPromise;
}

function loadHtmlImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Imagem inválida ou corrompida')); };
    img.src = url;
  });
}

function canvasHasTransparency(ctx: CanvasRenderingContext2D, w: number, h: number): boolean {
  // Amostragem em grid 8×8 — Lê todo o canvas custa muito em 4K. Pixels
  // semi-transparentes são raros num grid grosseiro só se o ativo é mesmo
  // transparente (logo, overlay), então 64 pontos é suficiente.
  const data = ctx.getImageData(0, 0, w, h).data;
  const stepX = Math.max(1, Math.floor(w / 8));
  const stepY = Math.max(1, Math.floor(h / 8));
  for (let y = 0; y < h; y += stepY) {
    for (let x = 0; x < w; x += stepX) {
      const alpha = data[(y * w + x) * 4 + 3];
      if (alpha < 255) return true;
    }
  }
  return false;
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => b ? resolve(b) : reject(new Error('Falha ao codificar imagem otimizada')),
      type,
      quality,
    );
  });
}

async function optimizeImage(file: File, kind: MediaKind): Promise<File> {
  if (!MIME_HINTS[kind].test(file.type)) return file;
  if (SKIP_OPTIMIZE.has(file.type)) return file;

  const img = await loadHtmlImage(file);
  const scale = Math.min(IMAGE_MAX_SIDE / img.width, IMAGE_MAX_SIDE / img.height, 1);
  const w = Math.max(1, Math.round(img.width * scale));
  const h = Math.max(1, Math.round(img.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { alpha: file.type === 'image/png' });
  if (!ctx) return file;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, w, h);

  // Decide formato de saída em ordem de preferência:
  //   1. WebP — ~30% menor que JPEG, decoda barato no Android moderno
  //      (expo-image e WebView falam WebP nativo). Suporta alpha, então
  //      cobre tanto fotos quanto logos transparentes.
  //   2. PNG  — fallback só se o browser não tem encoder WebP e a imagem
  //      original era PNG com transparência (logos / overlays).
  //   3. JPEG — fallback final pra fotos opacas em browsers sem WebP.
  const isPng = file.type === 'image/png';
  const hasAlpha = isPng && canvasHasTransparency(ctx, w, h);
  const canWebP = await browserCanEncodeWebP();

  let outType: string;
  let quality: number | undefined;
  if (canWebP) {
    outType = 'image/webp';
    quality = IMAGE_WEBP_QUALITY;
  } else if (hasAlpha) {
    outType = 'image/png';
    quality = undefined;
  } else {
    outType = 'image/jpeg';
    quality = IMAGE_JPEG_QUALITY;
  }

  const blob = await canvasToBlob(canvas, outType, quality);

  // Se a "otimização" deixou maior (pode acontecer com JPG já comprimido
  // sendo re-encodado), volta o original.
  if (blob.size >= file.size && w === img.width && h === img.height) return file;

  const baseName = file.name.replace(/\.[^.]+$/, '');
  const newExt =
    outType === 'image/webp' ? '.webp'
    : outType === 'image/jpeg' ? '.jpg'
    : '.png';
  return new File([blob], baseName + newExt, { type: outType, lastModified: Date.now() });
}

const VIDEO_MAX_WIDTH = 1920;
const VIDEO_MAX_HEIGHT = 1080;

interface VideoMeta {
  width: number;
  height: number;
  duration: number;
}

function probeVideo(file: File): Promise<VideoMeta> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const el = document.createElement('video');
    el.preload = 'metadata';
    el.muted = true;
    el.src = url;
    const cleanup = () => {
      URL.revokeObjectURL(url);
      el.removeAttribute('src');
      el.load();
    };
    el.onloadedmetadata = () => {
      const meta = {
        width: el.videoWidth,
        height: el.videoHeight,
        duration: Number.isFinite(el.duration) ? el.duration : 0,
      };
      cleanup();
      resolve(meta);
    };
    el.onerror = () => {
      cleanup();
      reject(new Error('Não foi possível ler o vídeo. Verifique se o formato é válido (MP4 H.264 recomendado).'));
    };
  });
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percent: number;
}

/**
 * Faz upload de um arquivo para o Supabase Storage usando RLS por path.
 * 1. Pede ao backend o bucket + path (path inclui tenant_id, validado por RLS).
 * 2. Faz upload direto pelo cliente Supabase com o JWT do usuário.
 * 3. Retorna a URL pública do arquivo.
 */
export async function uploadMedia(
  file: File,
  type: MediaKind,
  onProgress?: (p: UploadProgress) => void,
): Promise<string> {
  if (!MIME_HINTS[type].test(file.type)) {
    throw new Error(`Tipo de arquivo inválido para "${type}": ${file.type || 'desconhecido'}`);
  }
  if (file.size > SIZE_LIMITS[type]) {
    const mb = (SIZE_LIMITS[type] / 1024 / 1024).toFixed(0);
    throw new Error(`Arquivo excede ${mb}MB`);
  }

  if (type === 'video') {
    const meta = await probeVideo(file);
    const longSide = Math.max(meta.width, meta.height);
    const shortSide = Math.min(meta.width, meta.height);
    if (longSide > VIDEO_MAX_WIDTH || shortSide > VIDEO_MAX_HEIGHT) {
      throw new Error(
        `Resolução ${meta.width}×${meta.height} acima do permitido. Use no máximo ${VIDEO_MAX_WIDTH}×${VIDEO_MAX_HEIGHT} (1080p, paisagem ou retrato).`,
      );
    }
  }

  // Otimização (só para imagens). Faz ANTES de assinar upload pra não gastar
  // URL e pra mostrar progresso real ao usuário enquanto o canvas processa.
  let uploadFile = file;
  if (type !== 'video') {
    onProgress?.({ loaded: 0, total: file.size, percent: 5 });
    try {
      uploadFile = await optimizeImage(file, type);
    } catch (e: any) {
      throw new Error(e?.message || 'Falha ao processar imagem');
    }
    onProgress?.({ loaded: uploadFile.size, total: uploadFile.size, percent: 25 });
  }

  const safeName = uploadFile.name.replace(/[^\w.-]+/g, '_');
  const { data: meta } = await apiClient.getUploadUrl(type, safeName);

  // R2: backend assina URL PUT, frontend faz upload direto. XHR em vez de fetch
  // porque só XHR emite progresso real de upload no browser.
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', meta.upload_url);
    // Content-Type tem que bater com o que foi usado na assinatura.
    xhr.setRequestHeader('Content-Type', meta.content_type || uploadFile.type);
    xhr.upload.onprogress = (e) => {
      if (!e.lengthComputable) return;
      // 25–100% do progresso visual (otimização já consumiu 0–25%)
      const pct = 25 + Math.round((e.loaded / e.total) * 75);
      onProgress?.({ loaded: e.loaded, total: e.total, percent: pct });
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Falha no upload (status ${xhr.status}): ${xhr.responseText?.slice(0, 200) || ''}`));
    };
    xhr.onerror = () => reject(new Error('Erro de rede durante upload'));
    xhr.ontimeout = () => reject(new Error('Timeout no upload'));
    xhr.send(uploadFile);
  });

  onProgress?.({ loaded: uploadFile.size, total: uploadFile.size, percent: 100 });
  return meta.public_url;
}
